export interface IUserPhone {
  name: string
  value: string
  id: string
}

export interface IPhonePreferences {
  currentSelection: string
  phones: IUserPhone[]
}

export enum DateColumnFormatEnum {
  YYYYMMDD,
  USCustomaryWithTrailingMidnight,
}

export enum ColumnSettingTypeEnum {
  copyColumn = 'copyColumn',
  reformatDate = 'reformatDate',
  combineColumns = 'combineColumns',
}

export interface IColumnSetting {
  uuid: string
  sequence: number
  type: string
  description: string
  typeSpecificConfiguration: Record<string, string>
}

export interface IDDFField {
  name: string
  description: string
  editComponent: string
}

export interface IDDFColumnSetting {
  type: ColumnSettingTypeEnum
  description: string
  explanation: string
  fields: IDDFField[]
  fieldsForKeysIn: string[]
  fieldsForKeysOut: string[]
}

export interface IVersioned {
  version: number
}

export interface ICsvPreviewCell {
  original?: string
  transformed: string
}

export interface ICsvPreview {
  header: ICsvPreviewCell[]
  cellGrid: ICsvPreviewCell[][]
  requiredButMissingColumns?: string[]
}

export interface ICsvPreviewSniffRequest {
  csv: string
}

export interface ICsvPreviewRequest {
  columnSettings: IColumnSetting[]
  csv: string
}

export interface IFileCenterSampleData extends IVersioned {
  csvText: string
}

export interface IFileCenterColumnSettings extends IVersioned {
  columnSettings: IColumnSetting[]
}

export interface IKvpMapGetKey {
  allKeys: string[]
  thisKey?: string
  thisMap?: IKvpMap
}

export interface IReplacement {
  old: string
  new: string
}

export interface IKvpMap {
  replacements: IReplacement[]
}

export interface IUserProfile {
  username: string
  email: string
  familyName: string
  givenName: string
  currentTenant: ITenant
  tenants: ITenant[]
  roles: IUserRole[]
  activatedMfaOptions: any[]
  preferredMfa: string
  activationStatus: ActivationStatusEnum
  passwordStatus: PasswordStatusEnum
  created: string
  cognitoPhoneNumber: string
  cognitoPhoneNumberVerified: VerifiedEnum
  emailVerified: VerifiedEnum
  assistedCallEnabled: boolean
}

export interface IUserPreference {
  key: UserPreferencesKeysEnum
  value: string
  customerId?: string
}

export interface ITenant {
  awsCognitoPoolId: string
  tntId: number          // Integer ID - USE IN URLs
  tenantId: string       // GUID - USE IN HTTP HEADERS
  description: string
  customerId: string
  customer: ICustomer
  eobFaxNumber: string
}

export interface ICustomer {
  created: string
  customerId: string
  customerName: string
  eobFaxNumber: string
  updated: string
}

export interface IUserRole {
  roleId: string
  description: string
}

export enum IUserRoleGuidEnum {
  manageUsers = '89da1b55-d8f6-44e0-aaf0-f231830bdbac',
  supervisor = '62a74821-e496-41a0-a5f8-17cd2d493562',
  pocCustomer = '5d0a2ac4-5e43-4656-8f6c-42c943621c3e',
  customerSuccess = '9a6e6ab7-9cb5-425d-9a9d-61fd3f731343',
  dataTeamMember = '9f64a14f-1725-44f6-b15d-5c496672cd0b',
  fileSharePermission = '051bd5e1-bd05-4b68-9646-4fb85eb20a9a',
  claimAccessPermission = '583a7a5a-4c31-4ccc-b7b2-13a6aee70875',
  batchUploadPermission = '62a74821-e496-41a0-a5f8-17cd2d493562',
  runAutoCheckPermission = 'ffdacacc-7e40-47be-b9ad-9965b106a64a',
  claimsAccess = '583a7a5a-4c31-4ccc-b7b2-13a6aee70875',
  fileShare = '051bd5e1-bd05-4b68-9646-4fb85eb20a9a',
  batchUpload = '62a74821-e496-41a0-a5f8-17cd2d493562',
  runAutoCheck = 'ffdacacc-7e40-47be-b9ad-9965b106a64a',
  dataMapping = '9a6e6ab7-9cb5-425d-9a9d-61fd3f731343',
  manageCustomer = '1c7e9c21-66c5-474f-93c9-dbcc8a16abe2',
  manageTenant = '0b00f0c2-19e4-4427-bde5-e0a7c00b3ce0',
  manageUser = '89da1b55-d8f6-44e0-aaf0-f231830bdbac',
  managePayerConfig = '4bd55911-befc-4a15-9e2b-6e5369bee61d',
  manageGenericRules = '8f3edee3-83fe-406a-9fa9-4a5c71777cbb',
  backendAdmin = '2cdf6ab3-4cfd-4953-8a8f-05cdba2391c9',
  payerConfigAdmin = '82a75218-67d5-4ad8-8943-bbdc77ca934c',
  apiAdmin = '2efbdad6-5f77-4e7f-bb67-6ab946f39bdc',
  superAdmin = '0a7f2c4e-3d61-4f5f-90bc-7e8a71a1d293',
}

export enum VerifiedEnum {
  verified = 'Verified',
  unverified = 'Unverified',
}

export enum ActivationStatusEnum {
  active = 'Ok',
  pendingPasswordReset = 'MustReset',
}

export enum PasswordStatusEnum {
  active = 'Ok',
}

export interface IAddUser {
  enableMfa?: boolean
  phone?: string
  email: string
}

export const DEFAULT_PAYER_VA_SOFTPHONE_KEY = 'payerVaSoftphone'
export const MAX_NUMBER_SOFTPHONE = 1

export enum SettingsConstEnum {
  usPhoneNumberLength = 14,
}
export enum UserPreferencesKeysEnum {
  softphonePhoneNumbers = 'SoftphonePhoneNumbers',
  softphoneSpeakerDeviceId = 'SoftphoneSpeakerDeviceId',
  softphoneMicrophoneDeviceId = 'SoftphoneMicrophoneDeviceId',
  claimsGridCommonFilters = 'claimsGridCommonFilters',
  claimsGridCommonSort = 'claimsGridCommonSort',
  claimsGridCommonColumns = 'claimsGridCommonColumns',
  batchProcessedFilters = 'batchProcessedFilters',
  batchProcessedSort = 'batchProcessedSort',
  eob_enabled = 'eob_enabled',
  eob_max_delivery_timeout_hrs = 'eob_max_delivery_timeout_hrs',
  outboundAiPreferredTenant = 'outbound-ai-preferred-tenant',
  outboundAiPreferredTntId = 'outbound-ai-preferred-tnt-id',
  outboundAiPreferredCustomer = 'outbound-ai-preferred-customer',
  fileCenterColumnSettingsPractice = 'fileCenterColumnSettingsPractice',
  fileCenterSettingsSampleData = 'fileCenterSettingsSampleData',
  AutoCheckPreferences = 'AutoCheckPreferences',
  isNewUser = 'isNewUser',
  logout_time = 'ideal_logout_time',
}
